import React, {useEffect, useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Tag} from "primereact/tag";
import {SelectButton} from "primereact/selectbutton";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {Checkbox} from "primereact/checkbox";
import {FileUpload} from "primereact/fileupload";
import {Message} from "primereact/message";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {CampaignService} from "../../service/CampaignService";
import {TemplateService} from "../../service/TemplateService";
import {ProductExcelImportService} from "../../service/ProductExcelImportService";
import {CleanupService} from "../../service/CleanupService";
import {DashboardService} from "../../service/DashboardService";
import {protectionStrategies} from "../../utils/Constants";
import {exportFile, isAdmin} from "../../utils/Utils";
import {HelpIcon} from "./OnboardingHelp";
import PricingModeGuide from "../Pricing/PricingModeGuide";
import DynamicPricingAuditCard from "../Pricing/DynamicPricingAuditCard";

const campaignService = new CampaignService();
const templateService = new TemplateService();
const productExcelImportService = new ProductExcelImportService();
const cleanupService = new CleanupService();
const dashboardService = new DashboardService();
const PricingOnboardingStep = ({t, refresh, embedded, wizardMode}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign, loadCampaigns} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;

    const [strategy, setStrategy] = useState(() => ({...(selectedCampaign?.pricing_strategy || {})}));
    const [overview, setOverview] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    // Bumped on every action recorded in the audit log so the card below
    // reloads without waiting for a campaign switch.
    const [auditReloadKey, setAuditReloadKey] = useState(0);

    const loadOverview = () => {
        if (!campaignId) return;
        setMetricsLoading(true);
        dashboardService.getOverview(campaignId).then(o => setOverview(o || null))
            .catch(() => {}).finally(() => setMetricsLoading(false));
    };
    useEffect(() => { loadOverview(); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sendConfirm, setSendConfirm] = useState(false);
    const [sending, setSending] = useState(false);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deactivateConfirm, setDeactivateConfirm] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const [supplierMinPriceConfirm, setSupplierMinPriceConfirm] = useState(false);
    const [applyingSupplierMinPrices, setApplyingSupplierMinPrices] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [showAutoUpdate, setShowAutoUpdate] = useState(false);
    const [autoUpdateBusy, setAutoUpdateBusy] = useState(null);   // 'pause' | 'resume'
    const [loadingAutoUpdateTemplate, setLoadingAutoUpdateTemplate] = useState(false);
    // {updated_count, not_found} from the last SKU-list upload.
    const [autoUpdateResult, setAutoUpdateResult] = useState(null);

    const reductionTypes = [{label: t('reprice.amount'), value: 'amount'}, {label: t('reprice.ratio'), value: 'ratio'}];
    const reductionDirections = [{label: t('reprice.low'), value: 1}, {label: t('reprice.high'), value: -1}];
    const statuses = [{label: t('actions.passive'), value: false}, {label: t('actions.active'), value: true}];
    const updateTypes = [{label: t('reprice.manuel_update'), value: false}, {label: t('reprice.auto_update'), value: true}];
    const noCompetitorTypes = [{label: t('yes_no.no'), value: false}, {label: t('yes_no.yes'), value: true}];
    const buyboxStepTypes = [{label: t('reprice.amount'), value: 'amount'}, {label: t('reprice.ratio'), value: 'percentage'}];
    const buyboxUpStepTypes = [{label: t('reprice.amount'), value: 'amount'}, {label: t('reprice.ratio'), value: 'percentage'}];
    const pricingModes = [
        {label: t('reprice.mode_normal'), value: false, desc: t('reprice.mode_normal_desc')},
        {label: t('reprice.mode_buybox'), value: true, desc: t('reprice.mode_buybox_desc')},
    ];
    const isBuyboxCampaign = !!(selectedCampaign && selectedCampaign.settings && selectedCampaign.settings.marketplace_api);
    const supplierModule = selectedCampaign?.settings?.supplier_module || {};
    const protections = protectionStrategies(t);

    const [form, setForm] = useState(() => {
        const ps = selectedCampaign?.pricing_strategy || {};
        const rv = ps.reduction_value === null || ps.reduction_value === undefined ? 0 : ps.reduction_value;
        return {
            reduction_type: ps.reduction_type || 'amount',
            reduction_direction: rv >= 0 ? 1 : -1,
            reduction_value: Math.abs(rv),
            protection_strategy: ps.protection_strategy || 'min_value',
            is_active: !!ps.is_active,
            is_auto_update: !!ps.is_auto_update,
            no_competitor_strategy: !!ps.no_competitor_strategy,
            guarantee_buybox: !!ps.guarantee_buybox,
            buybox_step: ps.buybox_step ?? null,
            buybox_step_type: ps.buybox_step_type || 'amount',
            // Legacy campaigns saved before these fields existed -- default them to
            // the values that reproduce the old one-way-down behavior exactly.
            abandon_margin_pct: ps.abandon_margin_pct ?? 0,
            patience_hours: ps.patience_hours ?? null,
            buybox_upward_probe: !!ps.buybox_upward_probe,
            buybox_up_step: ps.buybox_up_step ?? null,
            buybox_up_step_type: ps.buybox_up_step_type || 'amount',
        };
    });
    const set = (k, v) => setForm(prev => ({...prev, [k]: v}));
    // InputNumber's onValueChange gives null for an emptied field -- keep that as-is
    // for nullable fields (patience_hours, buybox_up_step); abandon_margin_pct is NOT
    // nullable (0 = always chase is the safe/legacy default), so it is coerced back to 0.
    const onNumberFieldChange = (key, value, zeroDefault) => {
        setForm(prev => ({...prev, [key]: (zeroDefault && (value === null || value === undefined)) ? 0 : value}));
    };

    const protectionLabel = (v) => (protections.find(p => p.value === (v || 'min_value')) || {}).label;

    const summary = () => {
        const ps = strategy || {};
        if (!ps.is_active) return {active: false, text: t('onboarding.pricing.passiveText')};
        const upd = ps.is_auto_update ? t('reprice.auto_update') : t('reprice.manuel_update');
        if (ps.guarantee_buybox) {
            const step = ps.buybox_step === null || ps.buybox_step === undefined ? 0 : ps.buybox_step;
            const stepStr = ps.buybox_step_type === 'percentage' ? '%' + step : step;
            return {active: true, text: t('onboarding.pricing.buyboxSummary')
                .replace('{step}', stepStr).replace('{upd}', upd)};
        }
        const rv = ps.reduction_value === null || ps.reduction_value === undefined ? 0 : ps.reduction_value;
        const dir = rv >= 0 ? t('reprice.low') : t('reprice.high');
        const valStr = ps.reduction_type === 'ratio' ? '%' + Math.abs(rv) : Math.abs(rv);
        return {active: true, text: t('onboarding.pricing.summary')
            .replace('{dir}', dir).replace('{val}', valStr)
            .replace('{prot}', protectionLabel(ps.protection_strategy)).replace('{upd}', upd)};
    };

    const save = () => {
        setSaving(true);
        const ps = {
            ...(selectedCampaign.pricing_strategy || {}),
            reduction_type: form.reduction_type,
            protection_strategy: form.protection_strategy,
            is_active: form.is_active,
            is_auto_update: form.is_auto_update,
            no_competitor_strategy: form.no_competitor_strategy,
            guarantee_buybox: form.guarantee_buybox,
            buybox_step: form.buybox_step,
            buybox_step_type: form.buybox_step_type,
            abandon_margin_pct: form.abandon_margin_pct,
            patience_hours: form.patience_hours,
            buybox_upward_probe: form.buybox_upward_probe,
            buybox_up_step: form.buybox_up_step,
            buybox_up_step_type: form.buybox_up_step_type,
            reduction_value: (form.reduction_value || 0) * form.reduction_direction,
        };
        campaignService.savePricingStrategy(campaignId, ps).then(() => {
            selectedCampaign.pricing_strategy = ps;
            setStrategy({...ps});
            loadOverview();
            setAuditReloadKey(k => k + 1);
            if (loadCampaigns) loadCampaigns();
            toast.current.show({severity: 'success', summary: t('product_detail.repricing'), detail: t('message_detail.successful')});
            if (refresh) refresh();
            setShowForm(false);
        }).catch(error => { setError(error); setError(null); }).finally(() => setSaving(false));
    };

    const sendPriceToSite = () => {
        setSending(true);
        campaignService.applyPriceSuggestions(campaignId).then(() => {
            toast.current.show({severity: 'success', summary: t('reprice.campaign_send_price_to_site'), detail: t('message_detail.successful')});
            setSendConfirm(false);
            setAuditReloadKey(k => k + 1);
        }).catch(error => { setError(error); setError(null); }).finally(() => setSending(false));
    };

    const downloadTemplate = () => {
        setLoadingTemplate(true);
        templateService.exportDynamicPricingTemplate(campaignId).then(res => {
            exportFile(res, 'dynamic_pricing_template.xlsx');
        }).catch(error => { setError(error); setError(null); }).finally(() => setLoadingTemplate(false));
    };

    const uploadExcel = ({files}) => {
        const [file] = files;
        // No options.clear(): the busy flag below remounts the FileUpload empty (see ExcelOps).
        setUploading(true);
        productExcelImportService.importDynamicPricing(campaignId, file).then(() => {
            toast.current.show({severity: 'success', summary: t('product_detail.bulk_repricing_update'), detail: t('message_detail.successful')});
            loadOverview();
            setAuditReloadKey(k => k + 1);
        }).catch(error => { setError(error); setError(null); }).finally(() => setUploading(false));
    };

    const downloadAutoUpdateTemplate = () => {
        setLoadingAutoUpdateTemplate(true);
        templateService.exportAutoUpdateTemplate(campaignId).then(res => {
            exportFile(res, 'auto_update_template.xlsx');
        }).catch(error => { setError(error); setError(null); }).finally(() => setLoadingAutoUpdateTemplate(false));
    };

    // SKU-list upload that stops (or restarts) the AUTOMATIC price sending for
    // the listed products. It writes no strategy: a product priced by the
    // campaign stays priced by the campaign, so a resume puts it back exactly
    // where it was.
    const uploadAutoUpdateList = (isPaused) => ({files}) => {
        const [file] = files;
        // No options.clear(): the busy flag below remounts the FileUpload empty (see ExcelOps).
        setAutoUpdateBusy(isPaused ? 'pause' : 'resume');
        setAutoUpdateResult(null);
        const request = isPaused
            ? productExcelImportService.pauseAutoUpdate(campaignId, file)
            : productExcelImportService.resumeAutoUpdate(campaignId, file);
        request.then((result) => {
            setAutoUpdateResult(result);
            toast.current.show({severity: 'success',
                summary: isPaused ? t('reprice.auto_update_pause_import') : t('reprice.auto_update_resume_import'),
                detail: t('reprice.auto_update_import_done', {number: result.updated_count})});
            setAuditReloadKey(k => k + 1);
        }).catch(error => { setError(error); setError(null); }).finally(() => setAutoUpdateBusy(null));
    };

    const deactivateNoStrategy = () => {
        setDeactivating(true);
        cleanupService.deactivateProductsWithoutDynamicPricingStrategy(campaignId).then(() => {
            toast.current.show({severity: 'success', summary: t('onboarding.pricing.deactivateNoStrategy'), detail: t('message_detail.successful')});
            setDeactivateConfirm(false);
            setAuditReloadKey(k => k + 1);
        }).catch(error => { setError(error); setError(null); }).finally(() => setDeactivating(false));
    };

    // Recomputes every product's minimum price from the supplier prices already
    // collected, without waiting for the day's supplier refresh to finish. The
    // backend only queues it, so the result shows up a few minutes later.
    const applySupplierMinPrices = () => {
        setApplyingSupplierMinPrices(true);
        campaignService.applySupplierMinPrices(campaignId).then(() => {
            toast.current.show({severity: 'success', summary: t('onboarding.pricing.supplierMinPriceTitle'),
                detail: t('onboarding.pricing.supplierMinPriceQueued')});
            setSupplierMinPriceConfirm(false);
        }).catch(error => { setError(error); setError(null); }).finally(() => setApplyingSupplierMinPrices(false));
    };

    const s = summary();

    const cardStyle = {border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column', gap: 8};

    return (
        <div>
            {!embedded && <>
                <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                    <i className="pi pi-dollar"/> {t('onboarding.step.pricing.label', 'Dinamik Fiyatlandırma')}
                </h2>
                <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.pricing.desc')}</p>
            </>}

            <Message severity="info" text={t('onboarding.pricing.banner')} style={{display: 'block', marginBottom: 12}}/>

            {/* Metrics strip */}
            {(() => {
                const o = overview || {};
                const tiles = [
                    {key: 'templateBased', value: o.total_template_based_pricing_strategy, color: '#0891b2', icon: 'pi pi-clone'},
                    {key: 'campaignLevel', value: o.total_campaign_level_pricing_strategy, color: '#2563eb', icon: 'pi pi-send'},
                    {key: 'productLevel', value: o.total_product_level_pricing_strategy, color: '#7c3aed', icon: 'pi pi-sliders-h'},
                    {key: 'noCompetitor', value: o.total_products_without_competitors, color: '#9ca3af', icon: 'pi pi-minus-circle'},
                    {key: 'noMinProtection', value: o.total_no_min_price_protection, color: '#f59e0b', icon: 'pi pi-shield'},
                    {key: 'increase', value: o.total_higher_suggested_price, color: '#16a34a', icon: 'pi pi-arrow-up'},
                    {key: 'decrease', value: o.total_lower_suggested_price, color: '#dc2626', icon: 'pi pi-arrow-down'},
                ];
                return (
                    <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16}}>
                        {tiles.map(tile => (
                            <div key={tile.key} style={{flex: '1 1 110px', minWidth: 110, border: '1px solid #e5e7eb',
                                borderRadius: 10, padding: '10px 12px', background: '#fff'}}>
                                <div style={{fontSize: '1.5rem', fontWeight: 800, color: tile.color, display: 'flex', alignItems: 'center', gap: 8}}>
                                    <i className={tile.icon} style={{fontSize: '1.05rem'}}/>
                                    {metricsLoading ? '…' : (tile.value ?? 0)}
                                </div>
                                <small style={{color: '#6b7280'}}>{t('onboarding.pricing.metrics.' + tile.key)}</small>
                            </div>
                        ))}
                    </div>
                );
            })()}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14}}>
                {/* Strategy card */}
                <div style={cardStyle}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8}}>
                        <span style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                            <i className="pi pi-sliders-h" style={{color: '#2563eb'}}/> {t('onboarding.pricing.campaignStrategyTitle')}
                        </span>
                        <Tag value={s.active ? t('onboarding.scheduledJobs.active') : t('onboarding.scheduledJobs.inactive')}
                             severity={s.active ? 'success' : 'danger'}/>
                    </div>
                    <small style={{color: '#6b7280', flex: 1}}>{s.text}</small>
                    <Button label={t('onboarding.pricing.configure')} icon="pi pi-sliders-h"
                            className="p-button-outlined" onClick={() => setShowForm(true)}/>
                </div>

                {/* Bulk update card: single button opens a popup. Hidden in the
                    campaign creation wizard -- a campaign being created has no
                    products yet, so every uploaded code would match nothing. */}
                {!wizardMode && (
                <div style={cardStyle}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-file-excel" style={{color: '#15803d'}}/> {t('product_detail.bulk_repricing_update')}
                    </div>
                    <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.pricing.bulkHint')}</small>
                    <Button label={t('product_detail.bulk_repricing_update')} icon="pi pi-file-excel"
                            className="p-button-outlined p-button-success" onClick={() => setShowBulk(true)}/>
                </div>
                )}

                {/* Minimum prices from supplier prices: only meaningful when the
                    campaign's supplier module is on. Hidden in the wizard -- a new
                    campaign has no supplier prices yet. */}
                {supplierModule.is_active && !wizardMode && (
                <div style={cardStyle}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-shield" style={{color: '#f59e0b'}}/> {t('onboarding.pricing.supplierMinPriceTitle')}
                    </div>
                    <small style={{color: '#6b7280', flex: 1}}>
                        {t('onboarding.pricing.supplierMinPriceHint', {
                            sellers: supplierModule.seller_count || 3,
                            months: supplierModule.expiration_min_months || 6,
                            margin: supplierModule.margin_rate ?? 0})}
                    </small>
                    <Button label={t('onboarding.pricing.supplierMinPriceRun')} icon="pi pi-shield"
                            className="p-button-outlined p-button-warning" onClick={() => setSupplierMinPriceConfirm(true)}/>
                </div>
                )}

                {/* Per-product pause on automatic price sending, by SKU list.
                    Admin only: the merchant manages one product at a time from the
                    product's Repricing tab, the bulk list is run for them. Hidden in
                    the wizard for the same reason as the bulk update card above. */}
                {isAdmin() && !wizardMode && (
                <div style={cardStyle}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-pause" style={{color: '#d97706'}}/> {t('reprice.auto_update_sending')}
                    </div>
                    <small style={{color: '#6b7280', flex: 1}}>{t('reprice.auto_update_import_help')}</small>
                    <Button label={t('reprice.auto_update_sending')} icon="pi pi-pause"
                            className="p-button-outlined" onClick={() => setShowAutoUpdate(true)}/>
                </div>
                )}

                {/* Admin: deactivate products without strategy — hidden in the campaign creation wizard. */}
                {isAdmin() && !wizardMode && (
                    <div style={cardStyle}>
                        <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                            <i className="pi pi-ban" style={{color: '#dc2626'}}/> {t('onboarding.pricing.deactivateNoStrategy')}
                        </div>
                        <small style={{color: '#6b7280', flex: 1}}>{t('onboarding.pricing.deactivateHint')}</small>
                        <Button label={t('onboarding.pricing.deactivateNoStrategy')} icon="pi pi-ban"
                                className="p-button-outlined p-button-danger" onClick={() => setDeactivateConfirm(true)}/>
                    </div>
                )}
            </div>

            {/* Audit trail: who changed dynamic pricing, when and to what.
                Hidden during campaign creation -- a new campaign has no history. */}
            {!wizardMode && <DynamicPricingAuditCard t={t} campaignId={campaignId} reloadKey={auditReloadKey}/>}

            {/* Strategy form popup */}
            <Dialog header={t('product_detail.repricing')} visible={showForm} style={{width: '640px', maxWidth: '95vw'}} modal
                    onHide={() => !saving && setShowForm(false)}
                    footer={
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <Button label={t('reprice.send_price_to_site')} icon="pi pi-globe" className="p-button-outlined"
                                    onClick={() => setSendConfirm(true)}/>
                            <span>
                                <Button label={t('actions.cancel')} className="p-button-text" disabled={saving} onClick={() => setShowForm(false)}/>
                                <Button label={t('actions.save')} icon="pi pi-save" loading={saving} disabled={saving} onClick={save}/>
                            </span>
                        </div>
                    }>
                <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
                    {/* Buybox campaigns get a mode switch that swaps the strategy fields;
                        non-buybox campaigns just see the normal fields (no switch). */}
                    {isBuyboxCampaign &&
                    <div>
                        <div style={{fontSize: '.8rem', color: '#6b7280', marginBottom: 6}}>{t('reprice.pricing_mode')}</div>
                        <div style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
                            {pricingModes.map(m => {
                                const active = !!form.guarantee_buybox === m.value;
                                return (
                                    <div key={String(m.value)} onClick={() => set('guarantee_buybox', m.value)}
                                         style={{flex: '1 1 220px', minWidth: 200, cursor: 'pointer', borderRadius: 10, padding: '12px 14px',
                                                 border: '2px solid ' + (active ? '#2563eb' : '#e5e7eb'),
                                                 background: active ? '#eff6ff' : '#fff',
                                                 boxShadow: active ? '0 1px 4px rgba(37,99,235,.15)' : 'none'}}>
                                        <div style={{fontWeight: 700, color: '#111827', marginBottom: 4}}>{m.label}</div>
                                        <div style={{fontSize: '.8rem', color: '#6b7280'}}>{m.desc}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>}

                    <PricingModeGuide t={t} mode={!!(isBuyboxCampaign && form.guarantee_buybox)}/>

                    {isBuyboxCampaign && form.guarantee_buybox
                        ? <React.Fragment>
                            <div style={{fontWeight: 700, color: '#374151', marginTop: 4, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                                {t('reprice.section_no_buybox')}
                            </div>
                            <Row label={t('reprice.buybox_step')} help={t('reprice.buybox_step_tooltip')}>
                                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <InputNumber value={form.buybox_step} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                                 style={{width: '10em'}} onValueChange={(e) => set('buybox_step', e.value)}/>
                                    <SelectButton value={form.buybox_step_type} options={buyboxStepTypes} onChange={(e) => e.value && set('buybox_step_type', e.value)}/>
                                </span>
                            </Row>

                            <div style={{fontWeight: 700, color: '#374151', marginTop: 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                                {t('reprice.section_has_buybox')}
                            </div>
                            <Row label={t('reprice.buybox_upward_probe')} help={t('reprice.buybox_upward_probe_tooltip')}>
                                <Checkbox checked={!!form.buybox_upward_probe} onChange={(e) => set('buybox_upward_probe', e.checked)}/>
                            </Row>
                            <Row label={t('reprice.buybox_up_step')} help={t('reprice.buybox_up_step_tooltip')}>
                                <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                    <InputNumber value={form.buybox_up_step} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                                 style={{width: '10em'}} onValueChange={(e) => onNumberFieldChange('buybox_up_step', e.value)}/>
                                    <SelectButton value={form.buybox_up_step_type} options={buyboxUpStepTypes} onChange={(e) => e.value && set('buybox_up_step_type', e.value)}/>
                                </span>
                            </Row>

                            <div style={{fontWeight: 700, color: '#374151', marginTop: 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                                {t('reprice.section_cheap_seller')}
                            </div>
                            <Row label={t('reprice.abandon_margin_pct')} help={t('reprice.abandon_margin_pct_tooltip')}>
                                <InputNumber value={form.abandon_margin_pct} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2} suffix="%"
                                             style={{width: '10em'}} onValueChange={(e) => onNumberFieldChange('abandon_margin_pct', e.value, true)}/>
                            </Row>
                            <Row label={t('reprice.patience_hours')} help={t('reprice.patience_hours_tooltip')}>
                                <InputNumber value={form.patience_hours} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                             style={{width: '10em'}} onValueChange={(e) => onNumberFieldChange('patience_hours', e.value)}/>
                            </Row>
                          </React.Fragment>
                        : <React.Fragment>
                            <div style={{fontWeight: 700, color: '#374151', marginTop: 4, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                                {t('reprice.section_strategy')}
                            </div>
                            <Row label={t('reprice.reduction_type')}>
                                <SelectButton value={form.reduction_type} options={reductionTypes} onChange={(e) => e.value && set('reduction_type', e.value)}/>
                            </Row>
                            <Row label={t('reprice.reduction_direction')}>
                                <SelectButton value={form.reduction_direction} options={reductionDirections} onChange={(e) => e.value && set('reduction_direction', e.value)}/>
                            </Row>
                            <Row label={t('reprice.reduction_value')}>
                                <InputNumber value={form.reduction_value} min={0} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                             style={{width: '12em'}} onValueChange={(e) => set('reduction_value', e.value)}/>
                            </Row>

                            <div style={{fontWeight: 700, color: '#374151', marginTop: 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                                {t('reprice.section_protection')}
                            </div>
                            <Row label={t('reprice.protection_strategy')} help={t('reprice.protection_strategy_tooltip')}>
                                <Dropdown value={form.protection_strategy} options={protections} style={{width: '20em'}}
                                          onChange={(e) => set('protection_strategy', e.value)}/>
                            </Row>
                            <Row label={t('reprice.no_competitor_strategy')} help={t('reprice.no_competitor_strategy_tooltip')}>
                                <SelectButton value={form.no_competitor_strategy} options={noCompetitorTypes} onChange={(e) => e.value !== null && set('no_competitor_strategy', e.value)}/>
                            </Row>
                          </React.Fragment>}

                    <div style={{fontWeight: 700, color: '#374151', marginTop: 8, borderBottom: '1px solid #eef2f7', paddingBottom: 4}}>
                        {t('reprice.section_status')}
                    </div>
                    <Row label={t('reprice.is_active')}>
                        <SelectButton value={form.is_active} options={statuses} onChange={(e) => e.value !== null && set('is_active', e.value)}/>
                    </Row>
                    <Row label={t('reprice.is_auto_update')} help={t('reprice.send_price_to_site_tooltip')}>
                        <SelectButton value={form.is_auto_update} options={updateTypes} onChange={(e) => e.value !== null && set('is_auto_update', e.value)}/>
                    </Row>
                </div>
            </Dialog>

            {/* Bulk update popup: download template + upload */}
            <Dialog header={t('product_detail.bulk_repricing_update')} visible={showBulk} style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => !uploading && setShowBulk(false)}
                    footer={<Button label={t('actions.close')} className="p-button-text" disabled={uploading} onClick={() => setShowBulk(false)}/>}>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>{t('onboarding.pricing.bulkHint')}</small>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em'}}>1. {t('actions.download_template')}</div>
                        <Button label={t('actions.download_template')} icon="pi pi-download"
                                className="p-button-outlined p-button-success" loading={loadingTemplate} onClick={downloadTemplate}/>
                    </div>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em'}}>2. {t('product_detail.bulk_repricing_update')}</div>
                        {uploading
                            ? <Button label={t('product_detail.bulk_repricing_update')} icon="pi pi-spin pi-spinner" disabled/>
                            : <FileUpload customUpload mode="basic" auto accept=".xlsx,.xls"
                                          chooseLabel={t('product_detail.bulk_repricing_update')} uploadHandler={uploadExcel}/>}
                    </div>
                </div>
            </Dialog>

            {/* Pause / resume automatic price sending from a product-code list */}
            <Dialog header={t('reprice.auto_update_sending')} visible={showAutoUpdate} style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => !autoUpdateBusy && setShowAutoUpdate(false)}
                    footer={<Button label={t('actions.close')} className="p-button-text" disabled={!!autoUpdateBusy} onClick={() => setShowAutoUpdate(false)}/>}>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>{t('reprice.auto_update_import_help')}</small>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em'}}>1. {t('actions.download_template')}</div>
                        <Button label={t('actions.download_template')} icon="pi pi-download"
                                className="p-button-outlined p-button-success"
                                loading={loadingAutoUpdateTemplate} onClick={downloadAutoUpdateTemplate}/>
                        <small style={{display: 'block', color: '#6b7280', marginTop: '.35em'}}>
                            {t('reprice.auto_update_template_hint')}
                        </small>
                    </div>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em', display: 'flex', alignItems: 'center', gap: 6}}>
                            <i className="pi pi-pause" style={{color: '#d97706'}}/> 2. {t('reprice.auto_update_pause_import')}
                        </div>
                        {autoUpdateBusy === 'pause'
                            ? <Button label={t('reprice.auto_update_pause_import')} icon="pi pi-spin pi-spinner" disabled/>
                            : <FileUpload customUpload mode="basic" auto accept=".xlsx,.xls"
                                          chooseLabel={t('actions.upload')} uploadHandler={uploadAutoUpdateList(true)}/>}
                    </div>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em', display: 'flex', alignItems: 'center', gap: 6}}>
                            <i className="pi pi-play" style={{color: '#16a34a'}}/> 3. {t('reprice.auto_update_resume_import')}
                        </div>
                        {autoUpdateBusy === 'resume'
                            ? <Button label={t('reprice.auto_update_resume_import')} icon="pi pi-spin pi-spinner" disabled/>
                            : <FileUpload customUpload mode="basic" auto accept=".xlsx,.xls"
                                          chooseLabel={t('actions.upload')} uploadHandler={uploadAutoUpdateList(false)}/>}
                    </div>
                    {autoUpdateResult &&
                    <div>
                        <Message severity={autoUpdateResult.not_found.length > 0 ? "warn" : "success"}
                                 text={t('reprice.auto_update_import_result', {number: autoUpdateResult.updated_count, notFound: autoUpdateResult.not_found.length})}/>
                        {autoUpdateResult.not_found.length > 0 &&
                        <div style={{marginTop: '.5em', fontSize: '.8rem', color: '#6b7280', wordBreak: 'break-all'}}>
                            {autoUpdateResult.not_found.join(', ')}
                        </div>}
                    </div>}
                </div>
            </Dialog>

            {/* Send price to site confirm */}
            <Dialog header={t('reprice.send_price_to_site')} visible={sendConfirm} style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => !sending && setSendConfirm(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={sending} onClick={() => setSendConfirm(false)}/>
                            <Button label={t('reprice.send_price_to_site')} icon="pi pi-globe" loading={sending} onClick={sendPriceToSite}/>
                        </div>
                    }>
                <span>{t('dialogs.confirm_send_price_message')}</span>
            </Dialog>

            {/* Supplier minimum price confirm */}
            <Dialog header={t('onboarding.pricing.supplierMinPriceTitle')} visible={supplierMinPriceConfirm} style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => !applyingSupplierMinPrices && setSupplierMinPriceConfirm(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={applyingSupplierMinPrices}
                                    onClick={() => setSupplierMinPriceConfirm(false)}/>
                            <Button label={t('onboarding.pricing.supplierMinPriceRun')} icon="pi pi-check" className="p-button-warning"
                                    loading={applyingSupplierMinPrices} onClick={applySupplierMinPrices}/>
                        </div>
                    }>
                <Message severity="warn" text={t('onboarding.pricing.supplierMinPriceConfirm')} style={{display: 'block'}}/>
            </Dialog>

            {/* Deactivate confirm */}
            <Dialog header={t('onboarding.pricing.deactivateNoStrategy')} visible={deactivateConfirm} style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => !deactivating && setDeactivateConfirm(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text" disabled={deactivating} onClick={() => setDeactivateConfirm(false)}/>
                            <Button label={t('onboarding.cleanup.run')} icon="pi pi-check" className="p-button-danger" loading={deactivating} onClick={deactivateNoStrategy}/>
                        </div>
                    }>
                <div>
                    <p style={{marginTop: 0}}>{t('onboarding.pricing.deactivateHint')}</p>
                    <Message severity="warn" text={t('onboarding.cleanup.irreversible')} style={{display: 'block'}}/>
                </div>
            </Dialog>
        </div>
    );
};

// Labeled row for the form (label + optional help tooltip).
const Row = ({label, help, children}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
        <span style={{width: '14em', display: 'flex', alignItems: 'center', gap: 4, color: '#374151'}}>
            {label}{help && <HelpIcon text={help}/>}
        </span>
        {children}
    </div>
);

export default PricingOnboardingStep;
